import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  defaultDataIdFromObject
} from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { offsetLimitPagination } from '@apollo/client/utilities'

import { typesWithId } from './types-with-ids'

const csrfMeta = document.querySelector('meta[name=csrf-token]')
const csrfToken = csrfMeta?.getAttribute('content')

const batchLink = new BatchHttpLink({
  batchMax: 10,
  batchInterval: 20, // Wait no more than 20ms after first batched operation
  credentials: 'same-origin',
  headers: {
    'X-CSRF-Token': csrfToken || ''
  }
})

const retryLink = new RetryLink({
  delay: {
    initial: 100,
    max: 200,
    jitter: false
  },
  attempts: {
    max: 2,
    retryIf: (error) => !!error
  }
})

/**
 * Throw GraphQL errors so they are easier to detect (in development only)
 */
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        // eslint-disable-next-line i18next/no-literal-string
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
          locations
        )}, Path: ${JSON.stringify(path)}`
      )
    })
  // eslint-disable-next-line i18next/no-literal-string
  if (networkError) console.error(`[Network error]: ${networkError}`)
})

const createClient = (
  graphqlController = 'v1'
): ApolloClient<NormalizedCacheObject> => {
  const uri = graphqlController === 'v2' ? '/graphql_v2' : '/graphql'

  // Use HttpLink for v2 (no batching), BatchHttpLink for v1 (with batching)
  const httpLink =
    graphqlController === 'v2'
      ? new HttpLink({
          uri,
          credentials: 'same-origin',
          headers: { 'X-CSRF-Token': csrfToken || '' }
        })
      : batchLink

  let apolloLinks: ApolloLink[] = [retryLink, httpLink]

  if (process.env.NODE_ENV === 'development') {
    apolloLinks = [errorLink].concat(apolloLinks)
  }

  return new ApolloClient({
    uri,
    cache: new InMemoryCache({
      dataIdFromObject: (object, context) => {
        if (
          process.env.NODE_ENV === 'development' &&
          object.__typename &&
          !object.id &&
          typesWithId.includes(object.__typename)
        ) {
          console.warn(
            '%cMissing ID for %s',
            'font-size: 2rem; background-color: red; padding: 8px; color: white',
            object.__typename,
            object
          )
        }
        return defaultDataIdFromObject(object, context)
      },
      typePolicies: {
        User: {
          fields: {
            ordersByDate: offsetLimitPagination(['to', 'order']),
            orders: {
              ...offsetLimitPagination(),
              merge(existing = [], incoming, { args }) {
                // Check if the operation has both offset and limit
                if (args?.offset !== undefined && args?.limit !== undefined) {
                  // If offset is negative, add to the beginning
                  if (args.offset < 0) {
                    return [...incoming, ...existing]
                  }
                  // If offset is positive, add to the end
                  return [...existing, ...incoming]
                }

                // For regular queries, just use the server response
                return incoming
              }
            }
          }
        },
        Meal: {
          // Disable normalization for `Meal` type by not defining a unique identifier
          // for these entities. This will prevent Apollo Client from storing them
          // separately in the global cache.
          keyFields: false
        }
      }
    }),
    link: ApolloLink.from(apolloLinks)
  })
}

// Default client for backwards compatibility
const client = createClient()

export default client
export { createClient }
